/**
 * Client wrapper around the The Real Insight Browser Bot Chrome extension —
 * specifically the recording surface (long-lived port).
 *
 *   ping()              — verify the extension is installed/connectable
 *   startRecording(url) — open a tab at `url` and start capturing actions
 *   stopRecording()     — finish the active session, return final list
 *   onProgress(fn)      — subscribe to live action updates while recording
 *
 * The extension id is configured via `REACT_APP_TRI_BOT_EXTENSION_ID`
 * (matching the existing clientBot.ts pattern in this repo). When the env
 * var is missing OR the runtime isn't Chrome OR the extension isn't
 * installed, the methods reject with code 'unavailable' so the UI can
 * gracefully render an "Install the extension" hint.
 *
 * Connection model: one Port per session. We open the port lazily on
 * startRecording, listen for streaming server messages, resolve the
 * stop() promise on `recordingStopped`, reject on `recordingError`.
 */

import type {
  BrowserActionList,
  RecordingPortClientMessage,
  RecordingPortServerMessage,
} from "./bpm-protocol";

const RECORDING_PORT_NAME = "tri-bot.recording";

// Minimal local types for the chrome runtime surface we touch. We don't
// depend on @types/chrome (the rest of the portal's chrome-bot integration,
// e.g. clientBot.ts, also avoids it via `globalThis as any`).
type ChromePort = {
  postMessage(msg: unknown): void;
  disconnect(): void;
  onMessage: { addListener(cb: (msg: unknown) => void): void };
  onDisconnect: { addListener(cb: () => void): void };
};
type ChromeRuntime = {
  lastError?: { message?: string };
  connect(extensionId: string, info: { name: string }): ChromePort;
  sendMessage(
    extensionId: string,
    message: unknown,
    callback: (reply: unknown) => void,
  ): void;
};
function getChromeRuntime(): ChromeRuntime | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).chrome?.runtime as ChromeRuntime | undefined;
}

export type Connectivity =
  | { ok: true; capabilities: string[] }
  | { ok: false; reason: "no_extension_id" | "no_chrome" | "no_extension" | "error"; message?: string };

export type ProgressListener = (actions: BrowserActionList) => void;

export type StartRecordingResult = {
  sessionId: string;
  /** Resolves with the final action list when stopRecording() is called or
   *  the user closes the recorded tab. Rejects on recordingError. */
  done: Promise<BrowserActionList>;
  /** Subscribe to live updates. Returns an unsubscribe function. */
  onProgress: (fn: ProgressListener) => () => void;
  /** Trigger a graceful stop. */
  stop: () => void;
};

// CRA / webpack-DefinePlugin replaces `process.env.REACT_APP_*` only when the
// reference appears verbatim in source. Reading via an alias variable
// (`env.REACT_APP_*`) or via optional chaining (`process.env?.…`) silently
// yields undefined at runtime — so the var is referenced directly here.
const extensionId = process.env.REACT_APP_TRI_BOT_EXTENSION_ID || "";

function genSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export async function ping(): Promise<Connectivity> {
  if (!extensionId) return { ok: false, reason: "no_extension_id" };
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage) {
    return { ok: false, reason: "no_chrome" };
  }
  return await new Promise<Connectivity>((resolve) => {
    try {
      runtime.sendMessage(extensionId, { kind: "ping" }, (reply) => {
        if (runtime.lastError) {
          resolve({
            ok: false,
            reason: "no_extension",
            message: runtime.lastError.message,
          });
          return;
        }
        const r = reply as { kind?: string; capabilities?: string[] } | undefined;
        if (r && r.kind === "pong") {
          resolve({ ok: true, capabilities: r.capabilities ?? [] });
        } else {
          resolve({ ok: false, reason: "error", message: "unexpected reply" });
        }
      });
    } catch (e) {
      resolve({
        ok: false,
        reason: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  });
}

export function startRecording(url: string): StartRecordingResult {
  const runtime = getChromeRuntime();
  if (!extensionId || !runtime?.connect) {
    const err = new Error("Browser-bot extension is not connectable.");
    return {
      sessionId: "",
      done: Promise.reject(err),
      onProgress: () => () => undefined,
      stop: () => undefined,
    };
  }

  const sessionId = genSessionId();
  const listeners = new Set<ProgressListener>();

  let resolveDone: (a: BrowserActionList) => void;
  let rejectDone: (e: Error) => void;
  const done = new Promise<BrowserActionList>((res, rej) => {
    resolveDone = res;
    rejectDone = rej;
  });
  let doneSettled = false;
  let latestProgressActions: BrowserActionList = [];
  let hasProgressSnapshot = false;

  const settleDoneResolve = (actions: BrowserActionList) => {
    if (doneSettled) return;
    doneSettled = true;
    resolveDone(actions);
  };

  const settleDoneReject = (error: Error) => {
    if (doneSettled) return;
    doneSettled = true;
    rejectDone(error);
  };

  const port = runtime.connect(extensionId, { name: RECORDING_PORT_NAME });

  port.onMessage.addListener((raw) => {
    const msg = raw as RecordingPortServerMessage;
    switch (msg.kind) {
      case "recordingStarted":
        // No-op — we already optimistically know the sessionId.
        break;
      case "recordingProgress":
        latestProgressActions = msg.actions;
        hasProgressSnapshot = true;
        listeners.forEach((fn) => fn(msg.actions));
        break;
      case "recordingWarning":
        // Per-event capture issue (e.g. no accessible name). Forwarded to
        // listeners but NOT fatal — the session continues. Logged so the
        // modeler can see it during dev.
        // eslint-disable-next-line no-console
        console.warn(`[browser-bot recording] ${msg.code}: ${msg.message}`);
        break;
      case "recordingStopped":
        settleDoneResolve(msg.actions);
        try {
          port.disconnect();
        } catch {
          // ignore
        }
        break;
      case "recordingError":
        settleDoneReject(new Error(`${msg.code}: ${msg.message}`));
        try {
          port.disconnect();
        } catch {
          // ignore
        }
        break;
    }
  });

  port.onDisconnect.addListener(() => {
    if (doneSettled) return;
    // Disconnect can race with the final `recordingStopped` message. If we've
    // already seen streamed progress snapshots, treat the latest one as the
    // final result instead of surfacing a spurious failure in the panel.
    if (hasProgressSnapshot) {
      settleDoneResolve(latestProgressActions);
      return;
    }
    settleDoneReject(new Error("recording port disconnected"));
  });

  const startMsg: RecordingPortClientMessage = {
    kind: "startRecording",
    sessionId,
    url,
  };
  port.postMessage(startMsg);

  return {
    sessionId,
    done,
    onProgress: (fn) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    stop: () => {
      try {
        const stopMsg: RecordingPortClientMessage = { kind: "stopRecording", sessionId };
        port.postMessage(stopMsg);
      } catch {
        // ignore — likely already disconnected
      }
    },
  };
}

export const browserBotExtensionId = extensionId;
